import { BoardState } from '../types';
import { BASE64_CHARS, LANGUAGE_SETS } from './constants';

// Helper function to get word list for a language set
const getWordListForLanguage = (languageSetId: string): string[] => {
    const languageSet = LANGUAGE_SETS.find(set => set.id === languageSetId);
    return languageSet ? languageSet.words : LANGUAGE_SETS[0].words; // Default to first language set
};

// Convert big integer to base 64 string
const bigIntToBase64 = (num: bigint): string => {
    if (num === 0n) return BASE64_CHARS[0];

    let result = '';
    while (num > 0n) {
        result = BASE64_CHARS[Number(num % 64n)] + result;
        num = num / 64n;
    }
    return result;
};

// Convert base 64 string to big integer
const base64ToBigInt = (str: string): bigint => {
    let result = 0n;
    for (let i = 0; i < str.length; i++) {
        const charIndex = BASE64_CHARS.indexOf(str[i]);
        if (charIndex === -1) throw new Error('Invalid base 64 character');
        result = result * 64n + BigInt(charIndex);
    }
    return result;
};

// Encode board state to a compact string for URL
export const encodeBoardState = (state: BoardState): string => {
    const languageSetId = state.languageSetId || 'arabic'; // Default to arabic
    const wordList = getWordListForLanguage(languageSetId);
    const languageIndex = LANGUAGE_SETS.findIndex(set => set.id === languageSetId);

    // Convert all data to a single big integer
    let boardInt = 0n;

    // Pack language set index (2 bits, supports up to 4 language sets)
    boardInt = (boardInt << 2n) + BigInt(languageIndex >= 0 ? languageIndex : 0);

    // Pack word indices (25 words, max index depends on word list size, need 9 bits each)
    for (let i = 0; i < 25; i++) {
        const wordIndex = wordList.indexOf(state.words[i]);
        if (wordIndex === -1) {
            console.error(`Word not found in word list: ${state.words[i]}`);
            // Use index 0 as fallback to prevent undefined
            boardInt = (boardInt << 9n) + BigInt(0);
        } else {
            boardInt = (boardInt << 9n) + BigInt(wordIndex);
        }
    }

    // Pack card types (25 cards, 4 types, need 2 bits each)
    for (let i = 0; i < 25; i++) {
        const typeIndex = ['red', 'blue', 'neutral', 'assassin'].indexOf(state.types[i]);
        if (typeIndex === -1) {
            console.error(`Invalid card type: ${state.types[i]}`);
            // Use 0 (red) as fallback
            boardInt = (boardInt << 2n) + BigInt(0);
        } else {
            boardInt = (boardInt << 2n) + BigInt(typeIndex);
        }
    }

    return bigIntToBase64(boardInt);
};

// Decode board state from URL string
export const decodeBoardState = (encoded: string): BoardState | null => {
    try {
        let boardInt = base64ToBigInt(encoded);

        // Extract card types (50 bits, 2 bits each) - extracted in reverse order
        const types: string[] = [];
        const typeMap: string[] = ['red', 'blue', 'neutral', 'assassin'];
        for (let i = 0; i < 25; i++) {
            const typeIndex = Number(boardInt & 3n);
            if (typeIndex >= 0 && typeIndex < typeMap.length) {
                types.unshift(typeMap[typeIndex]);
            } else {
                console.error(`Invalid type index: ${typeIndex}`);
                types.unshift('neutral'); // Use neutral as fallback
            }
            boardInt = boardInt >> 2n;
        }

        // Extract word indices (225 bits, 9 bits each) - extracted in reverse order
        const words: string[] = [];
        // We need to extract language info first to get the right word list
        // For now, we'll extract all words then determine language from the word pattern
        const wordIndices: number[] = [];
        for (let i = 0; i < 25; i++) {
            const wordIndex = Number(boardInt & 511n); // 511 = 2^9 - 1
            wordIndices.unshift(wordIndex);
            boardInt = boardInt >> 9n;
        }

        // Extract language set index (2 bits)
        const languageIndex = Number(boardInt & 3n);
        const languageSet = LANGUAGE_SETS[languageIndex] || LANGUAGE_SETS[0];
        const wordList = languageSet.words;

        // Now convert word indices to actual words
        for (const wordIndex of wordIndices) {
            if (wordIndex >= 0 && wordIndex < wordList.length) {
                words.push(wordList[wordIndex]);
            } else {
                console.error(`Invalid word index: ${wordIndex}`);
                words.push(wordList[0]); // Use first word as fallback
            }
        }

        return {
            words,
            types: types as any, // Type assertion for CardType[]
            languageSetId: languageSet.id
        };
    } catch {
        return null;
    }
};

// Utility functions
export const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
};
